import { useRef, useState } from 'react';

import { useGenericModal } from '..';
import { FileConflictModal } from './file-conflict-modal';

/**
 * Owns the overwrite-confirmation modal and turns it into an awaitable gate:
 * `confirm(conflicts)` opens the modal and resolves to the user's choice, so an upload
 * handler can simply `if (!(await confirm(names))) return;`.
 */
export const useFileConflictModal = () => {
  const { modalRef, openModal, closeModal } = useGenericModal();
  const [conflicts, setConflicts] = useState<string[]>([]);
  const resolveRef = useRef<((proceed: boolean) => void) | null>(null);

  const settle = (proceed: boolean) => {
    resolveRef.current?.(proceed);
    resolveRef.current = null;
    setConflicts([]);
    closeModal();
  };

  const confirm = (names: string[]): Promise<boolean> => {
    // Nothing collides — no need to bother the user.
    if (!names.length) return Promise.resolve(true);

    setConflicts(names);
    openModal();
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  };

  const fileConflictModal = (
    <FileConflictModal
      modalRef={modalRef}
      conflicts={conflicts}
      onCancel={() => settle(false)}
      onOverwrite={() => settle(true)}
    />
  );

  return { confirm, fileConflictModal };
};
